/**
 * PRODUCT CONSTANTS
 *
 * Units are a fixed list rather than free text. If admins could type the unit
 * themselves you would end up with "kg", "Kg", "KG", "kilogram" and "kilo" all
 * meaning the same thing — which breaks filtering, sorting and search forever.
 */

export const PRODUCT_UNIT = Object.freeze({
  PIECE: 'PIECE',
  GRAM: 'GRAM',
  KILOGRAM: 'KILOGRAM',
  MILLILITRE: 'MILLILITRE',
  LITRE: 'LITRE',
  PACK: 'PACK',
  DOZEN: 'DOZEN',
  BOTTLE: 'BOTTLE',
  BOX: 'BOX',
});

/** Short forms shown on product cards: "500 g", "1 L", "1 pc". */
export const PRODUCT_UNIT_LABEL = Object.freeze({
  [PRODUCT_UNIT.PIECE]: 'pc',
  [PRODUCT_UNIT.GRAM]: 'g',
  [PRODUCT_UNIT.KILOGRAM]: 'kg',
  [PRODUCT_UNIT.MILLILITRE]: 'ml',
  [PRODUCT_UNIT.LITRE]: 'L',
  [PRODUCT_UNIT.PACK]: 'pack',
  [PRODUCT_UNIT.DOZEN]: 'dozen',
  [PRODUCT_UNIT.BOTTLE]: 'bottle',
  [PRODUCT_UNIT.BOX]: 'box',
});

/**
 * The size a customer sees next to the price — "500 g", "1 L", "1 box".
 *
 * Products carry two different things and only one of them is for customers:
 *   • `unitLabel` — the admin's "Display size" field. "500 g". What we want.
 *   • `unit`      — the enum above. "GRAM". A database value, not a size.
 *
 * Every surface had its own version of this choice and they disagreed, so the
 * same product read "500 g" on the website and "GRAM" in the app. One function
 * now decides it everywhere.
 *
 * A label of just "1" is completed from the unit instead of shown raw. Real
 * catalogue rows do this — "Khajoor box" and "pineapple rani" are both saved
 * as "1" — and beside a price a lone "1" reads as a quantity or a typo. The
 * number is trustworthy and the unit is known, so "1" + BOX becomes "1 box".
 *
 * It never invents a quantity: a product with no label falls back to the bare
 * unit ("pc"), because a missing size is not evidence that the size is one.
 */
export function productSizeLabel(product) {
  const shortUnit = () => {
    const unit = String(product?.unit ?? '').trim();
    if (!unit) return '';
    return PRODUCT_UNIT_LABEL[unit.toUpperCase()] ?? unit.toLowerCase();
  };

  const label = String(product?.unitLabel ?? '').trim();
  if (!label) return shortUnit();

  // Digits only ("1", "2.5") — a quantity that lost its unit.
  if (/^\d+(\.\d+)?$/.test(label)) {
    const unit = shortUnit();
    return unit ? `${label} ${unit}` : label;
  }

  return label;
}

/**
 * How products can be sorted on listing pages.
 * The frontend sends one of these keys; the backend maps it to a real
 * MongoDB sort. The client never sends raw database field names — that would
 * let anyone sort by internal fields and probe the schema.
 */
export const PRODUCT_SORT = Object.freeze({
  NEWEST: 'NEWEST',
  PRICE_LOW_TO_HIGH: 'PRICE_LOW_TO_HIGH',
  PRICE_HIGH_TO_LOW: 'PRICE_HIGH_TO_LOW',
  NAME_A_TO_Z: 'NAME_A_TO_Z',
  DISCOUNT: 'DISCOUNT',
});

/**
 * Curated homepage rails. Each maps to a different backend query.
 * Adding a new rail later means adding one entry here — not a new endpoint.
 */
export const PRODUCT_COLLECTION = Object.freeze({
  FEATURED: 'FEATURED',
  TRENDING: 'TRENDING',
  RECOMMENDED: 'RECOMMENDED',
  RECENTLY_ADDED: 'RECENTLY_ADDED',
});

/** Stock level below which the admin dashboard raises a "low stock" warning. */
export const LOW_STOCK_THRESHOLD = 10;

/** Guardrails for the admin product form. */
export const PRODUCT_LIMITS = Object.freeze({
  NAME_MIN: 2,
  NAME_MAX: 120,
  DESCRIPTION_MAX: 2000,
  MAX_IMAGES: 6,
  MAX_PRICE: 1_000_000, // ₹10 lakh — a sanity ceiling against typos
});
